import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FacilityClinicianMappingMetaData,
  FacilityClinicianMapping,
  FacilityClinicianMappingView,
} from '../services/FacilityClinicianMappingService';
import {
  getFacilityClinicianMappingMetaData,
  saveOrUpdateFacilityClinicianMapping,
  getFacilityClinicianMappingDetailsById,
  getFacilityClinicianMappingDetailsForView,
} from '../services/FacilityClinicianMappingService';

export const useGetFacilityClinicianMappingMetaData = (
  userId: string,
  facilityId: number,
  clinicianId: number,
  mappingId: number,
  action: string
) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<FacilityClinicianMappingMetaData>({
      queryKey: ['FacilityClinicianMappingMetaData', userId],
      queryFn: async () => {
        const data = await getFacilityClinicianMappingMetaData(
          userId,
          facilityId,
          clinicianId,
          mappingId,
          action
        );
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    });

  return {
    FacilityClinicianMappingMetaData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useSaveOrUpdateFacilityClinicianMapping = (
  onSuccess: () => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    FacilityClinicianMapping,
    Error,
    FacilityClinicianMapping & { userId: string; action: string }
  >({
    mutationFn: async ({ userId, action, ...payload }) => {
      const data = await saveOrUpdateFacilityClinicianMapping(
        userId,
        action,
        payload
      );
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ClinicianSpecificToFacilityListResponse'],
      });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error saving or updating partner:', error);
    },
  });
};

export const useGetFacilityClinicalMappingDetailsById = (
  userId: string,
  facilityClinicianMapId: number,
  action?: string
) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<FacilityClinicianMapping>({
      queryKey: [
        'FacilityClinicainMappingDetailsById',
        userId,
        facilityClinicianMapId,
      ],
      queryFn: async () => {
        const data = await getFacilityClinicianMappingDetailsById(
          userId,
          facilityClinicianMapId
        );
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: action === 'edit' && !!facilityClinicianMapId,
    });

  return {
    FacilityClinicainMappingDetailsById: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetFacilityClinicalMappingDetailsForView = (
  userId: string,
  facilityClinicianMapId: number
) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<FacilityClinicianMappingView>({
      queryKey: [
        'FacilityClinicainMappingDetailsForView',
        userId,
        facilityClinicianMapId,
      ],
      queryFn: async () => {
        const data = await getFacilityClinicianMappingDetailsForView(
          userId,
          facilityClinicianMapId
        );
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    });

  return {
    FacilityClinicainMappingDetailsForView: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
